import type { ReactNode } from "react";

import { cx } from "../lib/cx";
import type { CollaborationPeer } from "./session";

/** Two letters at most: initials read better than a truncated name at avatar size. */
export function initialsOf(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) {
    return "?";
  }
  const first = words[0]?.[0] ?? "";
  const last = words.length > 1 ? (words.at(-1)?.[0] ?? "") : "";
  return `${first}${last}`.toUpperCase();
}

export interface PresenceAvatarsProps {
  peers: readonly CollaborationPeer[];
  /** The user currently being followed, if any. */
  followingId?: string | null;
  /**
   * Clicking an avatar enters follow mode for that user and clicking it again leaves
   * (`docs/spec/collaboration.md` §10). Omitted, avatars are not interactive.
   */
  onFollow?: (peer: CollaborationPeer | null) => void;
  /** Avatars past this many collapse into a `+n` badge. */
  max?: number;
  className?: string;
}

/**
 * The presence row: one avatar per person on the document, the local user included so the
 * colour they write in is visible to them. Someone with three tabs open is one avatar, because
 * `peersFrom` has already de-duplicated by user id (§ edge case 6).
 */
export function PresenceAvatars({
  peers,
  followingId = null,
  onFollow,
  max = 5,
  className,
}: PresenceAvatarsProps): ReactNode {
  if (peers.length === 0) {
    return null;
  }
  const shown = peers.slice(0, max);
  const overflow = peers.length - shown.length;

  return (
    <div className={cx("flex items-center", className)} aria-label="People in this document">
      {shown.map((peer) => {
        const following = followingId === peer.id;
        const label = peer.isSelf ? `${peer.name} (you)` : peer.name;
        const avatar = (
          <span
            className={cx(
              "flex h-7 w-7 items-center justify-center rounded-full text-[11px]",
              "font-semibold text-white ring-2 ring-canvas",
              following && "ring-accent",
            )}
            style={{ backgroundColor: peer.color }}
          >
            {initialsOf(peer.name)}
          </span>
        );
        return onFollow === undefined ? (
          <span key={peer.id} className="-ml-1.5 first:ml-0" title={label}>
            {avatar}
          </span>
        ) : (
          <button
            key={peer.id}
            type="button"
            className="-ml-1.5 rounded-full first:ml-0"
            title={peer.isSelf ? label : `${label} — click to follow`}
            aria-label={label}
            aria-pressed={following}
            onClick={() => {
              onFollow(following ? null : peer);
            }}
          >
            {avatar}
          </button>
        );
      })}
      {overflow > 0 && (
        <span
          className={cx(
            "-ml-1.5 flex h-7 items-center justify-center rounded-full bg-raised px-2",
            "text-[11px] font-medium text-muted ring-2 ring-canvas",
          )}
        >
          {`+${String(overflow)}`}
        </span>
      )}
    </div>
  );
}
